import supabase from "@/supabase";
import * as Sentry from "@sentry/react";

export interface UploadResult {
  success: boolean;
  url?: string;
  message?: string;
}

const SIGNED_URL_EXPIRY = 3600;

export const extractStoragePath = (pathOrUrl: string): string => {
  if (!pathOrUrl) {
    return "";
  }
  const marker = "/object/public/application-documents/";
  const idx = pathOrUrl.indexOf(marker);
  if (idx !== -1) {
    return pathOrUrl.substring(idx + marker.length);
  }
  return pathOrUrl;
};

export const createSignedDocumentUrl = async (
  pathOrUrl: string
): Promise<string | null> => {
  if (!pathOrUrl) {
    return null;
  }
  const path = extractStoragePath(pathOrUrl);
  const { data, error } = await supabase.storage
    .from("application-documents")
    .createSignedUrl(path, SIGNED_URL_EXPIRY);
  if (error) {
    console.error("Error creating signed URL:", error);
    return null;
  }
  return data.signedUrl;
};

export const uploadFile = async (
  file: File,
  bucket: string,
  path: string
): Promise<UploadResult> => {
  try {
    const { error } = await supabase.storage.from(bucket).upload(path, file, {
      cacheControl: "3600",
      upsert: true,
    });

    if (error) {
      Sentry.captureException(error, {
        extra: {
          context: "storageService.uploadFile",
          bucket,
          path,
        },
      });
      console.error("Error uploading file:", error);
      return {
        success: false,
        message: `Error uploading file: ${error.message}`,
      };
    }

    return {
      success: true,
      url: path,
    };
  } catch (error) {
    console.error("Unexpected error in uploadFile:", error);
    return {
      success: false,
      message:
        error instanceof Error ? error.message : "An unexpected error occurred",
    };
  }
};

export const uploadApplicationDocument = async (
  file: File,
  applicationId: string,
  documentType: string
): Promise<UploadResult> => {
  if (!applicationId) {
    return {
      success: false,
      message: "Application ID is required",
    };
  }

  if (!file) {
    return {
      success: false,
      message: "File is required",
    };
  }

  const fileExtension = file.name.split(".").pop();
  const timestamp = new Date().getTime();
  const path = `${applicationId}/${documentType}-${timestamp}.${fileExtension}`;

  return uploadFile(file, "application-documents", path);
};

export const uploadCVDocument = async (
  file: File,
  firstName: string,
  lastName: string
): Promise<UploadResult> => {
  if (!file) {
    return {
      success: false,
      message: "File is required",
    };
  }

  if (!firstName || !lastName) {
    return {
      success: false,
      message: "First name and last name are required",
    };
  }

  const fileExtension = file.name.split(".").pop();
  const timestamp = new Date().getTime();
  const sanitizedFirstName = firstName.replace(/[^a-zA-Z0-9]/g, "");
  const sanitizedLastName = lastName.replace(/[^a-zA-Z0-9]/g, "");
  const path = `${sanitizedFirstName}-${sanitizedLastName}-${timestamp}.${fileExtension}`;

  return uploadFile(file, "cvs", path);
};

